<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentPayment.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('studentPayment.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3 col-sm-4">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('studentPayment.criteria') }}</legend>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('yearlyPaymentList.branchName')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        v-model.number="model.branch_id"
                        :options="branches"
                        @change="onBranchChange"
                      ></a-select>
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.class') }}</label>
                        <treeselect
                          :options="class_options"
                          :placeholder="$t('all')"
                          :multiple="true"
                          :limit="1"
                          v-model="model.class_id"
                          :maxHeight="150"
                        ></treeselect>
                        <div class="validate" v-if="errors.has('class_id')">
                          {{ errors.first('class_id') }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label class="required">{{
                          $t('studentPayment.dateRange')
                        }}</label>
                        <a-range-picker
                          format="DD-MM-YYYY"
                          :defaultValue="rangeDate"
                          :defaultPickerValue="rangeDate"
                          @change="dateRangeChange"
                        >
                          <template slot="renderExtraFooter"></template>
                        </a-range-picker>
                        <div class="validate" v-if="errors.has('date_range')">
                          {{ errors.first('date_range') }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label>{{ $t('studentPayment.studentStatus') }}</label>
                        <a-select
                          class="ts-w-full"
                          v-model="model.student_status_id"
                          :options="student_status"
                        ></a-select>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <label>{{
                        $t('studentEnrollmentReport.reportType')
                      }}</label>
                      <div class="form-group">
                        <div class="radio radio-css radio-inline">
                          <input
                            v-model="model.report_type"
                            type="radio"
                            id="summary"
                            :value="'Summary'"
                          />
                          <label for="summary">
                            {{ $t('report.summary') }}
                          </label>
                        </div>
                        <div class="radio radio-css radio-inline">
                          <input
                            v-model="model.report_type"
                            type="radio"
                            id="detail"
                            :value="'Details'"
                          />
                          <label for="detail">{{ $t('report.detail') }} </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          color="success"
                          class="btn-block"
                          @click.prevent="exportExcel"
                          :waiting="exporting"
                          ><i class="far fa-file-excel"></i>
                          {{ $t('export') }}</ts-button
                        >
                      </div>
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          color="danger"
                          class="btn-block"
                          @click.prevent="preview"
                          :waiting="waiting"
                          ><i class="far fa-file-pdf"></i>
                          {{ $t('preview') }}</ts-button
                        >
                      </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('studentPayment.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'studentPayment',
  data () {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      errors: new Errors(),
      class_options: [],
      branches: [],
      src: '',
      student_status: [
        { value: 0, label: 'All' },
        { value: 1, label: 'Active' },
        { value: 2, label: 'Dropfed out' },
        { value: 3, label: 'Suspend' },
        { value: 4, label: 'Terminated' }
      ],
      rangeDate: [moment(), moment()],
      model: {
        student_status_id: 0,
        branch_id: null,
        class_id: [],
        report_type: 'Summary'
      }
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    ...mapActions('reportPayment', ['getBranch', 'studentPayment', 'getClass', 'studentPaymentExcel']),
    moment,
    async fetchData () {
      this.loading = true
      await this.getBranch()
        .then(response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          this.model.branch_id = this.$store.state.authUser.default_branch_id
          this.onBranchChange()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    onBranchChange () {
      this.class_options = []
      this.model.class_id = []
      this.loading = true
      this.getClass({
        filter: {
          branch_id: this.model.branch_id
        },
        branch_id: [this.model.branch_id]
      })
        .then(response => {
          this.class_options = _.sortBy(response.data, ['class_name']).map(
            el => ({
              id: el.class_id,
              label:
                el.class_name +
                ' | ' +
                el.teacher.full_name_en +
                ' | ' +
                (el.academic_study_time ? el.academic_study_time.study_time : '')
            })
          )
          this.model.class_id = []
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    dateRangeChange (dateRange) {
      this.$store.commit(
        'reportPayment/SET_DATE_FROM',
        dateRange[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'reportPayment/SET_DATE_TO',
        dateRange[1].format('DD-MM-YYYY')
      )
    },
    exportExcel () {
      this.exporting = true
      this.errors = new Errors()

      this.studentPaymentExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'.indexOf('filename')]
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    preview () {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.studentPayment(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  }
}
</script>
